// Frontend client for the AWS S3 cross-account API.
// All requests go through the FastAPI backend — never STS/S3 directly.

import { headers } from "./classificationApi";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function j(method, path, body, attempt = 0) {
  const res = await fetch(`${BACKEND}/api${path}`, {
    method,
    headers: headers(null, !!body),
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; } catch {
    // Gateway HTML page (e.g. during a redeploy rollout): retry reads a few times before surfacing.
    if (method === "GET" && attempt < 3) {
      await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
      return j(method, path, body, attempt + 1);
    }
    const err = new Error(`The server is restarting or busy (HTTP ${res.status}) — wait a moment and refresh.`);
    err.status = res.status;
    throw err;
  }
  if (!res.ok) {
    const detail = (payload && payload.detail) || `HTTP ${res.status}`;
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return payload;
}

// ----- Config / onboarding --------------------------------------------------
export function s3Config() {
  return j("GET", "/s3/config");
}
export function s3Health() {
  return j("GET", "/s3/health");
}
export function s3Onboarding({ bucket, approved_prefix, external_id, region }) {
  return j("POST", "/s3/onboarding", { bucket, approved_prefix, external_id, region });
}

// ----- Connections ----------------------------------------------------------
export function listConnections() {
  return j("GET", "/s3/connections");
}
export function createConnection(payload) {
  return j("POST", "/s3/connections", payload);
}
export function testConnection(id) {
  return j("POST", `/s3/connections/${encodeURIComponent(id)}/test`);
}
export function deleteConnection(id) {
  return j("DELETE", `/s3/connections/${encodeURIComponent(id)}`);
}

// ----- Data plane -----------------------------------------------------------
export function listObjects(id, { prefix, limit = 200 } = {}) {
  const qs = new URLSearchParams();
  if (prefix) qs.set("prefix", prefix);
  if (limit) qs.set("limit", String(limit));
  return j("GET", `/s3/connections/${encodeURIComponent(id)}/objects?${qs.toString()}`);
}
export function inspectSchema(id, key) {
  const qs = new URLSearchParams({ key });
  return j("GET", `/s3/connections/${encodeURIComponent(id)}/schema?${qs.toString()}`);
}
export function sampleBytes(id, key, byteCap = 65536) {
  const qs = new URLSearchParams({ key, bytes: String(byteCap) });
  return j("GET", `/s3/connections/${encodeURIComponent(id)}/sample?${qs.toString()}`);
}

// ----- Bronze catalog -------------------------------------------------------
export function listBronze(connectionId) {
  const qs = connectionId ? `?connection_id=${encodeURIComponent(connectionId)}` : "";
  return j("GET", `/s3/bronze${qs}`);
}
export function stageBronze(payload) {
  return j("POST", "/s3/bronze", payload);
}
export function promoteBronze(assetId) {
  return j("POST", `/s3/bronze/${encodeURIComponent(assetId)}/promote`);
}
export function bulkPromoteBronze({ ids, connection_id, partition } = {}) {
  return j("POST", "/s3/bronze/bulk-promote", { ids: ids || [], connection_id, partition });
}
export function removeBronze(assetId) {
  return j("DELETE", `/s3/bronze/${encodeURIComponent(assetId)}`);
}

// ----- Gold aggregation ----------------------------------------------------
export function listGoldSources() {
  return j("GET", "/s3/gold/sources");
}
export function listGold() {
  return j("GET", "/s3/gold");
}
export function previewGold(payload) {
  return j("POST", "/s3/gold/preview", payload);
}
export function createGold(payload) {
  return j("POST", "/s3/gold", payload);
}
export function promoteGold(id) {
  return j("POST", `/s3/gold/${encodeURIComponent(id)}/promote`);
}
export function removeGold(id) {
  return j("DELETE", `/s3/gold/${encodeURIComponent(id)}`);
}

// ----- Scheduler -----------------------------------------------------------
export function schedulerStatus() {
  return j("GET", "/s3/scheduler/status");
}
export function schedulerRunNow() {
  return j("POST", "/s3/scheduler/run-now");
}

// ----- Audit ----------------------------------------------------------------
export function listAudit(connectionId, limit = 50) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (connectionId) qs.set("connection_id", connectionId);
  return j("GET", `/s3/audit?${qs.toString()}`);
}

// ----- Notifications --------------------------------------------------------
export function listNotifications({ role, unreadOnly = false, limit = 50 } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (role) qs.set("role", role);
  if (unreadOnly) qs.set("unread_only", "true");
  return j("GET", `/s3/notifications?${qs.toString()}`);
}
export function markNotificationRead(id) {
  return j("POST", `/s3/notifications/${encodeURIComponent(id)}/read`);
}
export function markAllNotificationsRead(role) {
  return j("POST", `/s3/notifications/read-all`, { role: role || null });
}

// ----- Auto-discovery (manual kick) ----------------------------------------
export function kickAutoDiscover(connectionId) {
  return j("POST", `/s3/connections/${encodeURIComponent(connectionId)}/auto-discover`);
}

// ----- Credential rotation (IT Head only) ----------------------------------
export async function rotateCredentials(
  connectionId,
  { aws_access_key_id, aws_secret_access_key, aws_session_token, session_duration_min = 60, actor, role },
) {
  const res = await fetch(
    `${BACKEND}/api/s3/connections/${encodeURIComponent(connectionId)}/rotate-credentials`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(actor ? { "X-Actor": actor } : {}),
        ...(role ? { "X-Actor-Role": role } : {}),
      },
      body: JSON.stringify({
        aws_access_key_id, aws_secret_access_key, aws_session_token,
        session_duration_min,
      }),
    },
  );
  const text = await res.text();
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; } catch {
    const err = new Error(`The server is restarting or busy (HTTP ${res.status}) — wait a moment and refresh.`);
    err.status = res.status;
    throw err;
  }
  if (!res.ok) {
    const detail = (payload && payload.detail) || `HTTP ${res.status}`;
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return payload;
}

// ----- Ingest Watch feed ---------------------------------------------------
export async function getIngestFeed({ connectionId, limit = 50 } = {}) {
  const q = new URLSearchParams();
  if (connectionId) q.set("connection_id", connectionId);
  q.set("limit", String(limit));
  const res = await fetch(`${BACKEND}/api/s3/ingest-feed?${q.toString()}`);
  if (!res.ok) throw new Error(`ingest-feed: HTTP ${res.status}`);
  return res.json();
}

// ----- Bronze preview (powers Silver thumbnails) ---------------------------
export async function getBronzePreview(assetId, { rows = 3 } = {}) {
  const res = await fetch(
    `${BACKEND}/api/s3/bronze/${encodeURIComponent(assetId)}/preview?rows=${rows}`,
  );
  const text = await res.text();
  // The ingress (Cloudflare) replaces 5xx JSON bodies with HTML error
  // pages, so we must JSON.parse defensively — swallow parse failures on
  // non-OK responses and surface the status + any detectable error hints
  // via err.status / err.looksCredentialRelated so the caller can branch.
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; } catch { /* HTML body */ }
  if (!res.ok) {
    const detail = (payload && payload.detail) || `HTTP ${res.status}`;
    const err = new Error(detail);
    err.status = res.status;
    // 502 from this endpoint typically wraps a backend AWS error. If the
    // ingress stripped the body, we still flag it as "likely credential-
    // related" when status is 502 so the UI can show the right guidance.
    const combined = (detail + " " + (text || "")).toLowerCase();
    err.looksCredentialRelated =
      res.status === 502 ||
      /expired|security token|credentials/.test(combined);
    throw err;
  }
  return payload;
}

export async function downloadAttestationPdf(connectionId, { days = 30, actor } = {}) {
  const qs = new URLSearchParams({ days: String(days) });
  if (actor) qs.set("actor", actor);
  const res = await fetch(
    `${BACKEND}/api/s3/connections/${encodeURIComponent(connectionId)}/attestation.pdf?${qs.toString()}`,
  );
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `attestation-${connectionId}-${days}d.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
